import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createCustomer } from '../services/api';
import useDialogFocus from '../hooks/useDialogFocus';
import ClientFields, { EMPTY_CLIENT, clientPayload } from './ClientFields';

/**
 * « Nouveau client » over a rendez-vous, rapport…: the full client record,
 * saved for good in the one list of clients, then handed back so the form can
 * select it. `entity` says which selector asked: "company" (client
 * facturation) or "customer" (client intervention).
 */
export default function NewClientDialog({ entity, initial, onCreated, onClose }) {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const [client, setClient] = useState({ ...EMPTY_CLIENT, ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useDialogFocus(dialogRef, { onEscape: () => { if (!saving) onClose(); } });
  // After the hook has moved focus into the dialog: start typing the name.
  useEffect(() => {
    dialogRef.current?.querySelector(`#new-${entity}-name`)?.focus();
  }, [entity]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!client.name.trim()) { setError(t('clients.nameRequired')); return; }
    setSaving(true);
    setError('');
    try {
      const created = await createCustomer(clientPayload({ ...client, name: client.name.trim() }));
      onCreated(created);
    } catch (err) {
      setError(err.response?.data?.detail || t('errors.generic'));
      setSaving(false);
    }
  };

  const titleId = `new-${entity}-title`;
  return (
    <div className="confirm-backdrop client-dialog-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form ref={dialogRef} tabIndex={-1} className="confirm-dialog client-dialog" role="dialog"
        aria-modal="true" aria-labelledby={titleId} onSubmit={handleSubmit} noValidate>
        <h2 id={titleId} className="confirm-title">{t('clients.newTitle', { context: entity })}</h2>
        <div className="client-dialog-body">
          <ClientFields value={client} onChange={setClient} idPrefix={`new-${entity}`} />
        </div>
        {error && <div className="ai-error" role="alert">{error}</div>}
        <div className="confirm-actions client-dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            {t('clients.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t('clients.creating') : t('clients.create', { context: entity })}
          </button>
        </div>
      </form>
    </div>
  );
}
